function normalizeForMatching(text) {
  if (!text) return '';
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

function normalizeForDisplay(name) {
  if (!name) return '';

  let normalized = name
    .replace(/ç/g, 'c')
    .replace(/ğ/g, 'g')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ş/g, 's')
    .replace(/ü/g, 'u')
    .replace(/Ç/g, 'C')
    .replace(/Ğ/g, 'G')
    .replace(/İ/g, 'I')
    .replace(/Ö/g, 'O')
    .replace(/Ş/g, 'S')
    .replace(/Ü/g, 'U');

  return normalized.replace(/\s+/g, ' ').trim();
}

function levenshteinDistance(s1, s2) {
  if (s1 === s2) return 0;
  if (!s1.length) return s2.length;
  if (!s2.length) return s1.length;

  const arr = [];
  for (let i = 0; i <= s2.length; i++) {
    arr[i] = [i];
  }
  for (let j = 0; j <= s1.length; j++) {
    arr[0][j] = j;
  }

  for (let i = 1; i <= s2.length; i++) {
    for (let j = 1; j <= s1.length; j++) {
      const cost = s1[j - 1] === s2[i - 1] ? 0 : 1;
      arr[i][j] = Math.min(
        arr[i - 1][j] + 1, // Deletion
        arr[i][j - 1] + 1, // Insertion
        arr[i - 1][j - 1] + cost // Substitution
      );
    }
  }
  return arr[s2.length][s1.length];
}

function getAuthorUniqueIdentifiers(item) {
  const ids = new Set();

  // E-posta(lar)ı işle
  const emailsToProcess = Array.isArray(item.email)
    ? item.email
    : item.email
      ? [item.email]
      : [];

  emailsToProcess.forEach(email => {
    let lowerEmail = email?.toLowerCase();

    let emailPrefix = lowerEmail.split('@')[0];
    ids.add(normalizeForMatching(emailPrefix.replace(/\d+$/, '')));

    // b. GitHub Noreply E-postaları: "12345678+username@users.noreply.github.com"
    const githubNoreplyMatch = lowerEmail.match(
      /^\d+\+([^@]+)@users\.noreply\.github\.com$/
    );
    if (githubNoreplyMatch && githubNoreplyMatch[1]) {
      ids.add(normalizeForMatching(githubNoreplyMatch[1])); // username kısmı
    } else {
      const githubIdMatch = lowerEmail.match(
        /^(\d+)@users\.noreply\.github\.com$/
      );
      if (githubIdMatch && githubIdMatch[1]) {
        ids.add(githubIdMatch[1]);
      }
    }

    if (lowerEmail.includes('+')) {
      let cleanedEmail =
        lowerEmail.substring(0, lowerEmail.indexOf('+')) +
        lowerEmail.substring(lowerEmail.indexOf('@'));
      ids.add(
        normalizeForMatching(cleanedEmail.split('@')[0].replace(/\d+$/, ''))
      );
    }

    if (lowerEmail.endsWith('@gmail.com')) {
      let gmailPrefix = lowerEmail.replace(/\./g, '').split('@')[0];
      ids.add(normalizeForMatching(gmailPrefix.replace(/\d+$/, '')));
    }

    ids.add(normalizeForMatching(lowerEmail.split('@')[0]));
  });

  if (item.author) {
    ids.add(normalizeForMatching(item.author));
  }

  const filteredIds = new Set();
  ids.forEach(id => {
    if (
      id &&
      id.length > 2 &&
      id !== 'githubbot' &&
      id !== 'bot' &&
      id !== 'unknown'
    ) {
      filteredIds.add(id);
    }
  });

  return filteredIds;
}

export function groupAuthors(authorList) {
  const grouped = {};
  const canonicalIdMap = new Map();

  authorList.forEach(item => {
    if (item.author_email) {
      item.email = item.author_email;
    }

    if (
      (!item.email || (Array.isArray(item.email) && item.email.length === 0)) &&
      !item.author
    ) {
      console.warn(
        `Uyarı: E-posta veya yazar adı bulunmayan kayıt atlandı: ${JSON.stringify(item)}`
      );
      return;
    }

    const itemIdentifiers = getAuthorUniqueIdentifiers(item);
    const normalizedAuthorName = normalizeForMatching(item.author);

    let foundCanonicalId = null;

    for (const id of itemIdentifiers) {
      if (canonicalIdMap.has(id)) {
        foundCanonicalId = canonicalIdMap.get(id);
        break;
      }
    }

    if (!foundCanonicalId && normalizedAuthorName) {
      for (const existingCanonicalId of canonicalIdMap.values()) {
        if (grouped[existingCanonicalId]) {
          const groupSampleItem =
            grouped[existingCanonicalId].find(i => i.author) ||
            grouped[existingCanonicalId][0];
          const groupSampleName = normalizeForMatching(
            groupSampleItem.author || ''
          );

          if (
            groupSampleName.length > 3 &&
            normalizedAuthorName.length > 3 &&
            levenshteinDistance(normalizedAuthorName, groupSampleName) <= 2
          ) {
            foundCanonicalId = existingCanonicalId;
            break;
          }
        }
      }
    }

    let currentCanonicalId;
    if (foundCanonicalId) {
      currentCanonicalId = foundCanonicalId;
    } else {
      currentCanonicalId =
        itemIdentifiers.size > 0
          ? Array.from(itemIdentifiers)[0]
          : normalizedAuthorName ||
            `unknown_${Math.random().toString(36).substr(2, 9)}`;

      if (currentCanonicalId.length < 3 && normalizedAuthorName.length >= 3) {
        currentCanonicalId = normalizedAuthorName;
      }

      if (
        currentCanonicalId.length < 3 &&
        item.email &&
        item.email.length > 0
      ) {
        const rawPrefixId = normalizeForMatching(
          Array.isArray(item.email)
            ? item.email[0].toLowerCase().split('@')[0]
            : item.email.toLowerCase().split('@')[0]
        );
        if (itemIdentifiers.has(rawPrefixId) && rawPrefixId.length >= 3) {
          currentCanonicalId = rawPrefixId;
        }
      }
    }

    for (const id of itemIdentifiers) {
      canonicalIdMap.set(id, currentCanonicalId);
    }

    if (normalizedAuthorName) {
      canonicalIdMap.set(normalizedAuthorName, currentCanonicalId);
    }

    if (!grouped[currentCanonicalId]) {
      grouped[currentCanonicalId] = [];
    }
    grouped[currentCanonicalId].push(item);
  });

  const finalGroupedAuthors = {};
  for (const canonicalId in grouped) {
    const groupItems = grouped[canonicalId];
    let primaryDisplayName = '';
    const nameCounts = {};

    groupItems.forEach(item => {
      const displayName = normalizeForDisplay(item.author || 'Bilinmiyor');
      nameCounts[displayName] = (nameCounts[displayName] || 0) + 1;
    });

    let maxCount = 0;
    for (const name in nameCounts) {
      if (nameCounts[name] > maxCount) {
        maxCount = nameCounts[name];
        primaryDisplayName = name;
      }
    }

    if (!primaryDisplayName || primaryDisplayName === 'Bilinmiyor') {
      let derivedName = canonicalId.replace(
        /_company|_gmail|_unknown|\d+/g,
        ''
      );
      derivedName = derivedName.charAt(0).toUpperCase() + derivedName.slice(1);
      if (derivedName && groupItems.length > 1) {
        primaryDisplayName = `${derivedName} (Grup)`;
      } else if (derivedName) {
        primaryDisplayName = derivedName;
      } else {
        primaryDisplayName = `Bilinmeyen Yazar (${canonicalId})`;
      }
    }

    finalGroupedAuthors[primaryDisplayName] = groupItems;
  }

  return finalGroupedAuthors;
}
